import React, { useEffect, useState } from 'react';
import { FaComments, FaExchangeAlt, FaDollarSign, FaEnvelope, FaClock } from 'react-icons/fa';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getDashboardOverview } from '../api';

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUtcTime, setCurrentUtcTime] = useState(() => new Date().toUTCString());

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getDashboardOverview();
        setDashboardData(response.data);
      } catch (err) {
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchOverview();

    const timer = setInterval(() => {
      setCurrentUtcTime(new Date().toUTCString());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Format UTC time properly - using built-in UTC methods
  const formatUtcTime = (utcString) => {
    const date = new Date(utcString);
    return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}:${date.getUTCSeconds().toString().padStart(2, '0')}`;
  };

  // Format date to show UTC
  const formatUtcDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { timeZone: 'UTC' }) + ' UTC';
  };

  // Defines dashboard statistics configuration
  const getStatsConfig = () => {
    if (!dashboardData?.kpiStats) return [];
    const kpiStats = dashboardData.kpiStats;
    return [
      {
        title: 'Total Conversations',
        value: kpiStats.total_conversations.toString(),
        icon: FaComments,
        change: `${kpiStats.conversations_change}%`,
        positive: kpiStats.conversations_change > 0,
        subtitle: 'vs last month'
      },
      {
        title: 'Avg Messages/Chat',
        value: kpiStats.avg_messages_per_chat.toString(),
        icon: FaExchangeAlt,
        change: `${kpiStats.avg_messages_change}%`,
        positive: kpiStats.avg_messages_change > 0,
        subtitle: 'vs last month'
      },
      {
        title: 'Credits Used This Month',
        value: kpiStats.total_cost_this_month.toLocaleString(),
        icon: FaDollarSign,
        change: kpiStats.remaining_balance.toLocaleString(),
        positive: false,
        subtitle: 'credits remaining'
      },
      {
        title: 'Messages Today',
        value: kpiStats.messages_today.toString(),
        icon: FaEnvelope,
        change: `${kpiStats.messages_today_change}`,
        positive: kpiStats.messages_today_change > 0,
        subtitle: 'vs yesterday'
      }
    ];
  };

  // Renders the dashboard header section with UTC time
  const renderHeader = () => (
    <div className="mb-8 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800">Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor your chatbot's performance and costs</p>
        </div>
        <div className="text-left sm:text-right">
          <div className="flex items-center text-gray-600 sm:justify-end">
            <FaClock className="mr-2" />
            <span className="font-mono">{formatUtcTime(currentUtcTime)} UTC</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">Daily data resets at 01:00 UTC</p>
          <p className="text-sm text-gray-500">All times displayed are in UTC</p>
          </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-14 h-14 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-gray-800 font-medium text-lg">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6">
      {renderHeader()}
      
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {getStatsConfig().map((stat, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6 w-full">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <stat.icon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-right">
                  <span className={`text-sm font-semibold ${stat.positive ? 'text-green-500' : 'text-blue-500'}`}>
                    {stat.change}
                  </span>
                  <p className="text-xs text-gray-500">{stat.subtitle}</p>
                </div>
              </div>
              <h2 className="text-gray-600 text-sm font-medium">{stat.title}</h2>
              <p className="text-2xl font-semibold text-gray-800 mt-2">{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="space-y-6 xl:space-y-0 xl:grid xl:grid-cols-2 xl:gap-6">
          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Monthly Conversations</h2>
            <div className="h-[300px] sm:h-[400px] -ml-4">
              {dashboardData?.monthlyConversations?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={dashboardData.monthlyConversations}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="day" 
                      tickFormatter={formatUtcDate}
                      tick={{ fontSize: 12 }}
                      angle={-15}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      labelFormatter={formatUtcDate}
                      contentStyle={{ fontSize: '12px' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#4169E1" 
                      fill="#4169E1" 
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  No conversation data available
                </div>
              )}
            </div>
      </div>

          <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Credits & Usage Overview</h2>
            <div className="h-[300px] sm:h-[400px] -ml-4">
              {dashboardData?.costUsageOverview?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={dashboardData.costUsageOverview}
                    margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="day" 
                      tickFormatter={formatUtcDate}
                      tick={{ fontSize: 12 }}
                      angle={-15}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis 
                      yAxisId="left" 
                      tick={{ fontSize: 12 }}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right"
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip 
                      labelFormatter={formatUtcDate}
                      contentStyle={{ fontSize: '12px' }}
                    />
                    <Legend 
                      wrapperStyle={{ fontSize: '12px', margin: '0 0 10px 0' }}
                      verticalAlign="top"
                      height={36}
                    />
                    <Line 
                      yAxisId="left" 
                      type="monotone" 
                      dataKey="cost" 
                      stroke="#4169E1" 
                      name="Daily Credits Used" 
                      dot={false} 
                      strokeWidth={2}
                    />
                    <Line 
                      yAxisId="right" 
                      type="monotone" 
                      dataKey="messages" 
                      stroke="#10B981" 
                      name="Messages" 
                      dot={false} 
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  No cost and usage data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;